/**
 * Bond positioning logic for arrows
 *
 * Handles calculations for arrows starting from or ending at bonds
 */

import { v3 } from "@alchemiesolns/maths";
import { calculateBondPerpendicularDirection } from "../core/geometry";
import { BOND_CONSTANTS } from "../core/constants";
import type { Point } from "../types";

/**
 * Configuration for bond positioning
 */
export interface BondPositioningConfig {
  /**
   * Perpendicular offset multiplier for determining bond side
   * Default: 1.2
   */
  perpendicularOffset?: number;

  /**
   * Extension distance for bond endpoint
   * Default: 1.2
   */
  endPointExtension?: number;
}

/**
 * Manages bond positioning for arrow calculations
 */
export class BondPositioner {
  private config: Required<BondPositioningConfig>;

  constructor(config: BondPositioningConfig = {}) {
    this.config = {
      perpendicularOffset:
        config.perpendicularOffset ?? BOND_CONSTANTS.PERPENDICULAR_OFFSET,
      endPointExtension:
        config.endPointExtension ?? BOND_CONSTANTS.END_POINT_EXTENSION,
    };
  }

  /**
   * Calculate the starting point for an arrow from a bond
   *
   * Chooses the bond endpoint closer to the destination
   *
   * @param bondPoint - Bond point data
   * @param destPos - Destination position
   * @returns Starting position for the arrow
   */
  public calculateStartPoint(bondPoint: Point, destPos: number[]): number[] {
    const bondDir = bondPoint.direction[0];
    const position = bondPoint.position;
    const offset = bondPoint.offset;

    const posOne = v3.addScaledVector(position, bondDir, offset);
    const posTwo = v3.addScaledVector(position, bondDir, -offset);

    const disOne = v3.distance(posOne, destPos);
    const disTwo = v3.distance(posTwo, destPos);

    return disOne > disTwo ? posTwo : posOne;
  }

  /**
   * Calculate the direction for an arrow starting from or ending at a bond
   *
   * Returns a perpendicular direction to the bond, on the side closer to oppPos
   *
   * @param bondPoint - Bond point data
   * @param oppPos - Opposite position (start if bond is destination, dest if bond is source)
   * @param camDir - Camera direction for cross product
   * @returns Direction perpendicular to bond
   */
  public calculateDirection(
    bondPoint: Point,
    oppPos: number[],
    camDir: number[]
  ): number[] {
    return calculateBondPerpendicularDirection(
      bondPoint.direction[0],
      bondPoint.position,
      oppPos,
      camDir,
      this.config.perpendicularOffset
    );
  }

  /**
   * Calculate the endpoint for an arrow ending at a bond
   *
   * Extends slightly beyond the bond position
   *
   * @param bondPoint - Bond point data
   * @param startPos - Arrow start position
   * @param direction - Direction vector to extend
   * @param camDir - Camera direction
   * @returns Final endpoint position
   */
  public calculateEndPoint(
    bondPoint: Point,
    startPos: number[],
    _direction: number[],
    camDir: number[]
  ): number[] {
    const dir = this.calculateDirection(bondPoint, startPos, camDir);
    return v3.addScaledVector(
      bondPoint.position,
      dir,
      this.config.endPointExtension
    );
  }

  /**
   * Get current configuration
   */
  public getConfig(): Readonly<Required<BondPositioningConfig>> {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<BondPositioningConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultBondPositioner = new BondPositioner();
